import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Paper, Typography, Box, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, IconButton, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  FormControl, InputLabel, Select, Grid, Tooltip,
  LinearProgress
} from '@mui/material';
import { Add, Edit, Delete, Refresh } from '@mui/icons-material';
import { toast } from 'react-toastify';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5006/api';

const CATEGORIES = [
  'CYBER_FOUNDATIONS',
  'LINUX_FUNDAMENTALS',
  'ATTACK_VECTORS',
  'DEFENSE_OPS',
  'CAPSTONE'
];

const DIFFICULTIES = ['easy', 'medium', 'hard'];

const emptyQuestion = {
  question_text: '',
  category: 'CYBER_FOUNDATIONS',
  difficulty: 'medium',
  options: { A: '', B: '', C: '', D: '' },
  correct_answer: 'A',
  explanation: '',
  points: 1
};

const QuestionManager = () => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ ...emptyQuestion });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [filterCategory, setFilterCategory] = useState('');

  const getToken = () => localStorage.getItem('adminToken');
  const authHeaders = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });

  const fetchQuestions = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/questions`, authHeaders());
      setQuestions(res.data);
    } catch (err) {
      toast.error('Failed to load questions');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => { fetchQuestions(); }, [fetchQuestions]);

  const filteredQuestions = filterCategory
    ? questions.filter(q => q.category === filterCategory)
    : questions;

  const handleOpenAdd = () => {
    setEditingId(null);
    setForm({ ...emptyQuestion });
    setDialogOpen(true);
  };

  const handleOpenEdit = (q) => {
    setEditingId(q.id);
    setForm({
      question_text: q.question_text,
      category: q.category,
      difficulty: q.difficulty,
      options: typeof q.options === 'string' ? JSON.parse(q.options) : q.options,
      correct_answer: q.correct_answer,
      explanation: q.explanation || '',
      points: q.points || 1
    });
    setDialogOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
    setEditingId(null);
    setForm({ ...emptyQuestion });
  };

  const handleOptionChange = (key, value) => {
    setForm(prev => ({
      ...prev,
      options: { ...prev.options, [key]: value }
    }));
  };

  const handleSave = async () => {
    if (!form.question_text.trim()) {
      toast.error('Question text is required');
      return;
    }
    if (!form.options.A.trim() || !form.options.B.trim() ||
        !form.options.C.trim() || !form.options.D.trim()) {
      toast.error('All four options (A-D) are required');
      return;
    }

    try {
      if (editingId) {
        await axios.put(`${API_URL}/admin/questions/${editingId}`, form, authHeaders());
        toast.success('Question updated');
      } else {
        await axios.post(`${API_URL}/admin/questions`, form, authHeaders());
        toast.success('Question created');
      }
      handleClose();
      fetchQuestions();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save question');
    }
  };

  const handleDeleteClick = (q) => {
    setDeleteTarget(q);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await axios.delete(`${API_URL}/admin/questions/${deleteTarget.id}`, authHeaders());
      toast.success('Question deleted');
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
      fetchQuestions();
    } catch (err) {
      toast.error('Failed to delete question');
    }
  };

  const difficultyColor = (d) => {
    switch (d) {
      case 'easy': return 'success';
      case 'medium': return 'warning';
      case 'hard': return 'error';
      default: return 'default';
    }
  };

  if (loading) return <LinearProgress />;

  return (
    <Paper elevation={3} sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h5">Question Bank</Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Filter Category</InputLabel>
            <Select value={filterCategory} label="Filter Category" onChange={e => setFilterCategory(e.target.value)}>
              <MenuItem value="">All Categories</MenuItem>
              {CATEGORIES.map(c => <MenuItem key={c} value={c}>{c.replace(/_/g, ' ')}</MenuItem>)}
            </Select>
          </FormControl>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchQuestions}><Refresh /></IconButton>
          </Tooltip>
          <Button variant="contained" startIcon={<Add />} onClick={handleOpenAdd}>
            Add Question
          </Button>
        </Box>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {filteredQuestions.length} question{filteredQuestions.length !== 1 ? 's' : ''}
        {filterCategory ? ` in ${filterCategory.replace(/_/g, ' ')}` : ''}
      </Typography>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Question</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Difficulty</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Answer</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Points</TableCell>
              <TableCell sx={{ fontWeight: 600, width: 100 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredQuestions.map((q, idx) => (
              <TableRow key={q.id} hover>
                <TableCell>{idx + 1}</TableCell>
                <TableCell sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  <Tooltip title={q.question_text}>
                    <span>{q.question_text}</span>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Chip label={q.category.replace(/_/g, ' ')} size="small" color="primary" variant="outlined" />
                </TableCell>
                <TableCell>
                  <Chip label={q.difficulty} size="small" color={difficultyColor(q.difficulty)} />
                </TableCell>
                <TableCell>{q.correct_answer}</TableCell>
                <TableCell>{q.points}</TableCell>
                <TableCell>
                  <IconButton size="small" onClick={() => handleOpenEdit(q)} color="primary">
                    <Edit fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleDeleteClick(q)} color="error">
                    <Delete fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {filteredQuestions.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography color="text.secondary" sx={{ py: 3 }}>
                    No questions found. Click "Add Question" to create one.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>{editingId ? 'Edit Question' : 'Add New Question'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth label="Question Text" multiline rows={3}
                value={form.question_text}
                onChange={e => setForm(prev => ({ ...prev, question_text: e.target.value }))}
                required
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select value={form.category} label="Category"
                  onChange={e => setForm(prev => ({ ...prev, category: e.target.value }))}>
                  {CATEGORIES.map(c => <MenuItem key={c} value={c}>{c.replace(/_/g, ' ')}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Difficulty</InputLabel>
                <Select value={form.difficulty} label="Difficulty"
                  onChange={e => setForm(prev => ({ ...prev, difficulty: e.target.value }))}>
                  {DIFFICULTIES.map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Correct Answer</InputLabel>
                <Select value={form.correct_answer} label="Correct Answer"
                  onChange={e => setForm(prev => ({ ...prev, correct_answer: e.target.value }))}>
                  {['A', 'B', 'C', 'D'].map(l => <MenuItem key={l} value={l}>Option {l}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Option A"
                value={form.options.A}
                onChange={e => handleOptionChange('A', e.target.value)}
                required
                error={form.correct_answer === 'A' && !form.options.A.trim()}
                helperText={form.correct_answer === 'A' ? 'Correct answer' : ''}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Option B"
                value={form.options.B}
                onChange={e => handleOptionChange('B', e.target.value)}
                error={form.correct_answer === 'B' && !form.options.B.trim()}
                helperText={form.correct_answer === 'B' ? 'Correct answer' : ''}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Option C"
                value={form.options.C}
                onChange={e => handleOptionChange('C', e.target.value)}
                error={form.correct_answer === 'C' && !form.options.C.trim()}
                helperText={form.correct_answer === 'C' ? 'Correct answer' : ''}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Option D"
                value={form.options.D}
                onChange={e => handleOptionChange('D', e.target.value)}
                error={form.correct_answer === 'D' && !form.options.D.trim()}
                helperText={form.correct_answer === 'D' ? 'Correct answer' : ''}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Explanation" multiline rows={2}
                value={form.explanation}
                onChange={e => setForm(prev => ({ ...prev, explanation: e.target.value }))}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField fullWidth label="Points" type="number" inputProps={{ min: 1 }}
                value={form.points}
                onChange={e => setForm(prev => ({ ...prev, points: parseInt(e.target.value) || 1 }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>
            {editingId ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Question</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this question?
          </Typography>
          {deleteTarget && (
            <Typography sx={{ mt: 1, fontStyle: 'italic', color: 'text.secondary' }}>
              "{deleteTarget.question_text?.substring(0, 100)}..."
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeleteConfirm}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default QuestionManager;
